"use client";

import { c as _c } from "react/compiler-runtime";
import { Box, Group, Stack, Text, UnstyledButton } from "@mantine/core";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { ScrollArea } from "@/shared/ui";
import { AccountNavigation } from "@/layouts/app-shell/navigation/account-navigation/account-navigation";
import { HamburgerIcon } from "@/layouts/app-shell/navigation/navigation-icons";
import { NavigationLogo } from "@/layouts/app-shell/navigation/navigation-logo/navigation-logo";
import { NavigationMenu } from "@/layouts/app-shell/navigation/navigation-menu/navigation-menu";
import { useSidebarNavGroups } from "@/layouts/app-shell/navigation/use-sidebar-nav-groups";
import { ADMIN_TEXTS } from "@/constants/admin-texts";
import { ADMIN_ROUTES } from "@/config/admin-routes";
import { useActiveInternalAccountsTotal } from "@/features/admin/users/api/internal-accounts-queries";
import { useActiveStreamerAccountsTotal } from "@/features/admin/streamers/api/streamer-accounts-queries";
import { INTERNAL_ROLES } from "@/features/auth/api/types";
import { PAGE_TITLES } from "@/hooks/usePageTracking";
import { cn } from "@/lib/utils";
import type { SidebarConfig } from "@/types/sidebar";
import classes from "./mobile-navigation.module.scss";
const MOBILE_PAGE_TITLES: Partial<Record<string, string>> = {
  "/": "Stream Stats",
  "/slots/library": "Slots Library",
  "/live-casino/library": "LC Library",
  "/roadmaps": "Roadmaps"
};
interface MobileNavigationProps {
  config?: SidebarConfig;
}
export function MobileNavigation(t0) {
  const $ = _c(56);
  const {
    config
  } = t0;
  const [isOpen, setIsOpen] = useState(false);
  const pathname = usePathname();
  const [panelOpen, setPanelOpen] = useState(false);
  const [prevPathname, setPrevPathname] = useState(pathname);
  if (prevPathname !== pathname) {
    setPrevPathname(pathname);
    setIsOpen(false);
  }
  const {
    sidebarConfig,
    groups,
    profile,
    isProfileLoading
  } = useSidebarNavGroups(config);
  let t1;
  if ($[0] !== pathname) {
    t1 = pathname.startsWith(ADMIN_ROUTES.streamers);
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isStreamers = t1;
  let t2;
  if ($[2] !== pathname) {
    t2 = pathname.startsWith(ADMIN_ROUTES.users);
    $[2] = pathname;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const isAdmins = t2;
  const t3 = !isStreamers;
  let t4;
  if ($[4] !== t3) {
    t4 = {
      skip: t3
    };
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const streamerCount = useActiveStreamerAccountsTotal(t4);
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = [...INTERNAL_ROLES];
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = !isAdmins;
  let t7;
  if ($[7] !== t6) {
    t7 = {
      skip: t6
    };
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const adminCount = useActiveInternalAccountsTotal(t5, t7);
  let pageTitle = MOBILE_PAGE_TITLES[pathname] ?? PAGE_TITLES[pathname] ?? (pathname.startsWith("/news/") ? "News" : "StreamStats");
  if (isStreamers) {
    pageTitle = `${ADMIN_TEXTS.list.allStreamers} (${streamerCount})`;
  } else {
    if (isAdmins) {
      pageTitle = `${ADMIN_TEXTS.list.allAdmins} (${adminCount})`;
    }
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = open => {
      setPanelOpen(open);
      if (open) {
        setIsOpen(false);
      }
    };
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  const handlePanelOpenChange = t8;
  let t10;
  let t9;
  if ($[10] !== isOpen) {
    t9 = () => {
      document.body.style.overflow = isOpen ? "hidden" : "";
      return _temp;
    };
    t10 = [isOpen];
    $[10] = isOpen;
    $[11] = t10;
    $[12] = t9;
  } else {
    t10 = $[11];
    t9 = $[12];
  }
  useEffect(t9, t10);
  const LogoIcon = sidebarConfig.streamerHubInfo?.icon;
  let t11;
  if ($[13] !== panelOpen) {
    t11 = () => {
      if (panelOpen) {
        return;
      }
      setIsOpen(true);
    };
    $[13] = panelOpen;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <HamburgerIcon className={classes.menuIcon} aria-hidden={true} />;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t11) {
    t13 = <UnstyledButton type="button" onClick={t11} className={classes.menuButton} aria-label="Open navigation menu">{t12}</UnstyledButton>;
    $[16] = t11;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== LogoIcon) {
    t14 = LogoIcon && <Box className={classes.logoWrap}><LogoIcon aria-label="StreamHub" className={classes.logoImage} /></Box>;
    $[18] = LogoIcon;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== pageTitle) {
    t15 = <Text component="span" className={classes.pageTitle} truncate={true}>{pageTitle}</Text>;
    $[20] = pageTitle;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== t13 || $[23] !== t14 || $[24] !== t15) {
    t16 = <Box component="header" className={classes.header}><Group className={classes.headerStart} gap="sm" wrap="nowrap">{t13}{t14}{t15}</Group></Box>;
    $[22] = t13;
    $[23] = t14;
    $[24] = t15;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  const t17 = isOpen ? classes.overlayOpen : classes.overlayClosed;
  let t18;
  if ($[26] !== t17) {
    t18 = cn(classes.overlay, t17);
    $[26] = t17;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  const t19 = !isOpen;
  let t20;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Box className={classes.backdrop} onClick={() => setIsOpen(false)} role="presentation" />;
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  const t21 = isOpen ? classes.drawerOpen : classes.drawerClosed;
  let t22;
  if ($[29] !== t21) {
    t22 = cn(classes.drawer, t21);
    $[29] = t21;
    $[30] = t22;
  } else {
    t22 = $[30];
  }
  let t23;
  if ($[31] !== sidebarConfig.streamerHubInfo) {
    t23 = sidebarConfig.streamerHubInfo && <Box className={classes.logoRow}><NavigationLogo logo={sidebarConfig.streamerHubInfo.logo} variant="full" /></Box>;
    $[31] = sidebarConfig.streamerHubInfo;
    $[32] = t23;
  } else {
    t23 = $[32];
  }
  let t24;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => setIsOpen(false);
    $[33] = t24;
  } else {
    t24 = $[33];
  }
  let t25;
  if ($[34] !== groups) {
    t25 = <ScrollArea className={classes.navScroll}><NavigationMenu groups={groups} onClose={t24} forceExpanded={true} /></ScrollArea>;
    $[34] = groups;
    $[35] = t25;
  } else {
    t25 = $[35];
  }
  let t26;
  if ($[36] !== isProfileLoading || $[37] !== panelOpen || $[38] !== profile) {
    t26 = profile && <AccountNavigation user={profile} isLoading={isProfileLoading} panelOpen={panelOpen} onPanelOpenChange={handlePanelOpenChange} />;
    $[36] = isProfileLoading;
    $[37] = panelOpen;
    $[38] = profile;
    $[39] = t26;
  } else {
    t26 = $[39];
  }
  let t27;
  if ($[40] !== t26) {
    t27 = <Box className={classes.userFooter}>{t26}</Box>;
    $[40] = t26;
    $[41] = t27;
  } else {
    t27 = $[41];
  }
  let t28;
  if ($[42] !== t23 || $[43] !== t25 || $[44] !== t27) {
    t28 = <Stack className={classes.drawerInner} gap={0}>{t23}{t25}{t27}</Stack>;
    $[42] = t23;
    $[43] = t25;
    $[44] = t27;
    $[45] = t28;
  } else {
    t28 = $[45];
  }
  let t29;
  if ($[46] !== t22 || $[47] !== t28) {
    t29 = <Box className={t22}>{t28}</Box>;
    $[46] = t22;
    $[47] = t28;
    $[48] = t29;
  } else {
    t29 = $[48];
  }
  let t30;
  if ($[49] !== t18 || $[50] !== t19 || $[51] !== t29) {
    t30 = <Box className={t18} aria-hidden={t19}>{t20}{t29}</Box>;
    $[49] = t18;
    $[50] = t19;
    $[51] = t29;
    $[52] = t30;
  } else {
    t30 = $[52];
  }
  let t31;
  if ($[53] !== t16 || $[54] !== t30) {
    t31 = <>{t16}{t30}</>;
    $[53] = t16;
    $[54] = t30;
    $[55] = t31;
  } else {
    t31 = $[55];
  }
  return t31;
}
function _temp() {
  document.body.style.overflow = "";
}